import ms from 'ms';
import { useCallback, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchLatestStreamId, fetchStreamIdIfNeeded, getStreamStatus } from '@/redux/modules/video';

// Poll quickly until a stream is acquired, then keep watching at a slower cadence.
const ACQUIRE_INTERVAL_MS = ms('1s');
const WATCH_INTERVAL_MS = ms('5s');

/**
 * Keeps a catalog's streamId fresh in redux and returns it.
 *
 * - Until a stream exists, polls quickly to acquire one.
 * - Once a stream exists, keeps watching at a slower cadence. A broadcast that
 *   drops and restarts on the server is issued a NEW streamId
 *   (`catalog-<id>-<timestamp>`); the watch picks it up, the changed id flows to
 *   WebRTCPlayer as a prop, and the player reconnects to the new stream. This is
 *   what makes the stream auto-recover without a manual page refresh.
 *
 * Replaces the 1s `pollForAuctionRestart` that was baked into caterwaul's
 * AuctionVideo. While the id is unchanged the watch is a no-op for the player
 * (same prop value), so steady-state playback is undisturbed.
 */
export const useStreamIdPolling = (catalogId: number) => {
    const dispatch = useAppDispatch();
    const { streamId } = useAppSelector((state) => getStreamStatus(state, catalogId));

    useEffect(() => {
        const intervalMs = streamId ? WATCH_INTERVAL_MS : ACQUIRE_INTERVAL_MS;
        const interval = setInterval(() => {
            dispatch(fetchStreamIdIfNeeded(catalogId));
        }, intervalMs);
        return () => {
            clearInterval(interval);
        };
    }, [catalogId, dispatch, streamId]);

    return streamId;
};

/**
 * Returns a callback that fetches and resolves to the latest streamId for a
 * catalog. Pass to WebRTCPlayer's `fetchStreamId` so it refreshes the id before
 * each reconnect attempt — a broadcast that drops and restarts on the server is
 * issued a new streamId, so reconnecting with the old one would hit a dead stream.
 */
export const useFetchStreamId = (catalogId: number) => {
    const dispatch = useAppDispatch();
    return useCallback(() => dispatch(fetchLatestStreamId(catalogId)), [dispatch, catalogId]);
};
